import { OverlapDetails } from './priorityCalculator'
import { toLocalISOString } from './shared'
import { CalendarEvent } from '../context/Data'

export type RecurringConflictOps = {
	updateExisting?: Partial<CalendarEvent>
	addEvents?: CalendarEvent[]
	deleteExisting?: boolean
}

function startOfDay(d: Date) {
	const x = new Date(d)
	x.setHours(0, 0, 0, 0)
	return x
}

/**
 * Resolves a conflict where the EXISTING slot is a recurring series.
 *
 * Goal: only the conflict day is affected and the existing series DTSTART is NOT overwritten.
 * This prevents “losing” the series start date when a later occurrence is edited via overlap resolution.
 */
export function resolveAgainstRecurringSeries(
	details: OverlapDetails
): RecurringConflictOps | null {
	const {
		existingEvent,
		overlapStart,
		overlapEnd,
		existingStart,
		existingEnd,
		scenario,
	} = details

	if (!existingEvent.rrule) return null

	const seriesStart = existingEvent.rrule.dtstart
		? startOfDay(new Date(existingEvent.rrule.dtstart))
		: null
	const conflictDay = startOfDay(overlapStart)

	if (!seriesStart) return null

	// Only needed when conflict is on/after the series start; otherwise existing logic is fine.
	if (conflictDay.getTime() < seriesStart.getTime()) return null

	const durationMs = existingEnd.getTime() - existingStart.getTime()

	const oneOff = (
		segStart: Date,
		segEnd: Date,
		suffix: string
	): CalendarEvent => ({
		...existingEvent,
		id: `${existingEvent.id}-${suffix}-${Date.now()}`,
		start: segStart,
		end: segEnd,
		timeslotId: undefined,
		timeslotContentId: undefined,
		rrule: undefined,
		duration: undefined,
		startTime: undefined as any,
		repeatDays: [],
	})

	const continuation = (suffix: string): CalendarEvent => {
		const dtstart = new Date(conflictDay)
		dtstart.setDate(dtstart.getDate() + 1)
		dtstart.setHours(existingStart.getHours(), existingStart.getMinutes(), 0, 0)

		return {
			...existingEvent,
			id: `${existingEvent.id}-${suffix}-${Date.now()}`,
			start: dtstart,
			end: new Date(dtstart.getTime() + durationMs),
			timeslotId: undefined,
			timeslotContentId: undefined,
			rrule: {
				...existingEvent.rrule,
				dtstart: toLocalISOString(dtstart),
				...(existingEvent.rrule?.until
					? { until: existingEvent.rrule.until }
					: {}),
			},
			startTime: `${String(dtstart.getHours()).padStart(2, '0')}:${String(dtstart.getMinutes()).padStart(2, '0')}:00`,
			duration: { milliseconds: durationMs },
		}
	}

	const ops: RecurringConflictOps = { addEvents: [] }
	const shiftedStart = conflictDay.getTime() === seriesStart.getTime()

	if (!shiftedStart) {
		const dayBefore = new Date(conflictDay)
		dayBefore.setDate(dayBefore.getDate() - 1)
		dayBefore.setHours(23, 59, 59, 999)
		ops.updateExisting = {
			rrule: {
				...existingEvent.rrule,
				until: toLocalISOString(dayBefore),
			},
		}
	} else {
		// conflict on series start day: shift series DTSTART to the day after conflict.
		const dtstart = new Date(conflictDay)
		dtstart.setDate(dtstart.getDate() + 1)
		dtstart.setHours(existingStart.getHours(), existingStart.getMinutes(), 0, 0)
		ops.updateExisting = {
			start: dtstart,
			end: new Date(dtstart.getTime() + durationMs),
			rrule: { ...existingEvent.rrule, dtstart: toLocalISOString(dtstart) },
			startTime: `${String(dtstart.getHours()).padStart(2, '0')}:${String(dtstart.getMinutes()).padStart(2, '0')}:00`,
			duration: { milliseconds: durationMs },
		}
	}

	const maybeAddContinuation = () => {
		if (shiftedStart) return
		ops.addEvents!.push(continuation('continuation'))
	}

	if (scenario === 'new_covers_existing') {
		maybeAddContinuation()
		return ops
	}

	if (scenario === 'new_overlaps_start') {
		ops.addEvents!.push(oneOff(overlapEnd, existingEnd, 'adjusted'))
		maybeAddContinuation()
		return ops
	}

	if (scenario === 'new_inside_existing') {
		// Split the conflict day into two one-off segments around the new slot.
		// This preserves both "before" and "after" portions of the existing slot.
		if (overlapStart.getTime() > existingStart.getTime()) {
			ops.addEvents!.push(oneOff(existingStart, overlapStart, 'first'))
		}
		if (existingEnd.getTime() > overlapEnd.getTime()) {
			ops.addEvents!.push(oneOff(overlapEnd, existingEnd, 'remainder'))
		}
		maybeAddContinuation()
		return ops
	}

	if (scenario === 'new_overlaps_end') {
		ops.addEvents!.push(oneOff(existingStart, overlapStart, 'shortened'))
		maybeAddContinuation()
		return ops
	}

	return null
}

// Backwards-compatible wrapper name (older call sites)
export function resolveOneTimeAgainstRecurringSeries(
	details: OverlapDetails,
	newSlotIsRecurring: boolean
): RecurringConflictOps | null {
	// When the *new/edited* slot is recurring and it conflicts with an existing recurring series,
	// the user expectation for the "cut" option is a GLOBAL recurring adjustment (time-of-day),
	// not a single-day split around the conflict date.
	//
	// The date-splitting logic in resolveAgainstRecurringSeries is correct for one-time conflicts
	// against a recurring series, but it leaves recurring-vs-recurring collisions unresolved
	// and will still fail the pre-save collision check.
	if (newSlotIsRecurring && details.existingEvent.rrule) {
		const {
			existingEvent,
			existingStart,
			existingEnd,
			overlapStart,
			overlapEnd,
			scenario,
		} = details

		const durationMs = existingEnd.getTime() - existingStart.getTime()
		const seriesDtstart = existingEvent.rrule?.dtstart
		if (!seriesDtstart || durationMs <= 0) return null

		// Build an updated DTSTART that keeps the original series start DATE
		// but changes the TIME-OF-DAY to match the cut result.
		const base = new Date(seriesDtstart)
		const setTime = (d: Date, timeSrc: Date) => {
			const x = new Date(d)
			x.setHours(timeSrc.getHours(), timeSrc.getMinutes(), 0, 0)
			return x
		}

		if (scenario === 'new_overlaps_start') {
			// Existing should start after the new slot ends.
			const newStart = overlapEnd
			const newDuration = existingEnd.getTime() - newStart.getTime()
			if (newDuration <= 0) return { deleteExisting: true }

			const dtstart = setTime(base, newStart)
			return {
				updateExisting: {
					start: undefined as any,
					end: undefined as any,
					rrule: { ...existingEvent.rrule, dtstart: toLocalISOString(dtstart) },
					startTime: `${String(newStart.getHours()).padStart(2, '0')}:${String(newStart.getMinutes()).padStart(2, '0')}:00`,
					duration: { milliseconds: newDuration },
				},
				addEvents: [],
			}
		}

		if (scenario === 'new_overlaps_end') {
			// Existing should end before the new slot starts.
			const newEnd = overlapStart
			const newDuration = newEnd.getTime() - existingStart.getTime()
			if (newDuration <= 0) return { deleteExisting: true }

			return {
				updateExisting: {
					start: undefined as any,
					end: undefined as any,
					// DTSTART unchanged (time stays as-is); just shrink duration.
					duration: { milliseconds: newDuration },
				},
				addEvents: [],
			}
		}

		if (scenario === 'new_covers_existing') {
			return { deleteExisting: true, addEvents: [] }
		}

		// new_inside_existing requires splitting the recurring series into two recurring windows
		// (which is a different operation). Fall back to legacy behavior for now.
	}

	return resolveAgainstRecurringSeries(details)
}
